import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Input,
  Box,
  FormLabel,
  List,
  ListItem,
  Spinner,
  Image,
  Flex,
  Text,
} from "@chakra-ui/react";
import debounce from "debounce";

import http from "../../../core/http";
import Downshift from "downshift";

const itemToString = (item) => item?.title || ``;

const incrementIssueNumber = (title) => {
  const regex = /#(\d+)$/;
  const match = title.match(regex);

  if (match) {
    const issueNumber = parseInt(match[1], 10);
    return title.replace(regex, `#${issueNumber + 1}`);
  } else {
    return title;
  }
};

/**
 * New Autocomplete without react-select
 */
const MyAutocomplete = ({
  franchise = 1,
  comic,
  index,
  onSelectionChange,
  isLeadingInput,
  lastComic,
}) => {
  const [value, setValue] = useState(comic ? comic.title : "");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [editMode, setEditMode] = useState(true);
  const [tabPressedOnce, setTabPressedOnce] = useState(false);
  const [selected, setSelected] = useState(comic ? comic : {});
  const [menuOpen, setMenuOpen] = useState(false);

  const fetchSuggestions = useCallback(
    (query) => {
      setLoading(true);
      http
        .post(`/v1/search?franchise=${franchise}`, { search: query })
        .then((response) => {
          setSuggestions(response.data.data);
          setLoading(false);
          setMenuOpen(true);
        })
        .catch((error) => {
          console.error("Error fetching suggestions:", error);
          // Handle error appropriately
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [franchise]
  );

  const throttledLoadOptions = useMemo(
    () => debounce(fetchSuggestions, 500),
    [fetchSuggestions]
  );

  useEffect(() => {
    if (comic && !editMode) {
      setValue(comic.title);
      setSelected(comic);
      setSuggestions([]);
      return;
    }

    if (!value) {
      setSuggestions([]);
      return;
    }

    throttledLoadOptions(value);

    // Cleanup function to cancel the debounced call
    return () => {
      throttledLoadOptions.clear();
    };
  }, [value, comic, editMode, throttledLoadOptions]);

  // Make sure if input value is cleared, when you get back in edit more it set it back
  useEffect(() => {
    if (editMode && selected) {
      setValue(selected.title);
    }
  }, [editMode, selected]);

  // Handle state changes
  const handdleStateChange = (changes) => {
    if (changes.hasOwnProperty("selectedItem")) {
      if (!isLeadingInput) {
        setSelected(changes.selectedItem);
        if (onSelectionChange && changes.selectedItem) {
          onSelectionChange(index, changes.selectedItem, comic);
        }
      } else {
        if (changes.selectedItem) {
          onSelectionChange(index, changes.selectedItem, comic);
        }
        setTabPressedOnce(false);
        setSelected(null);
        setValue("");
      }
    }
  };

  // Handle tab key press to get next issue
  const handleKeyDown = (event) => {
    if (event.key === "Tab" || event.keyCode === 9) {
      if (!tabPressedOnce) {
        event.preventDefault();
        setTabPressedOnce(true);
        const newTitle = incrementIssueNumber(lastComic.title);
        setValue(newTitle);
        fetchSuggestions(newTitle);
      } else {
        setTabPressedOnce(false);
      }
    }
  };

  return (
    <Box>
      {/* initialSelectedItem={yourDefaultItem} // Set the default selected item here */}
      {comic && !!comic.thumbnail && !editMode ? (
        <Flex w="100%" justifyContent="space-between" alignItems="center">
          <Box
            color="white"
            display="flex"
            alignItems="center"
            p="2"
            flex="1"
            borderWidth="2px"
            borderColor="transparent"
            onClick={() => {
              setEditMode(true);
            }}
          >
            <Image
              src={comic.thumbnail}
              alt={comic.title}
              style={{
                width: "30px",
                height: "50px",
                marginRight: "10px",
              }}
            />
            <Text pl="17px">{comic.title}</Text>
          </Box>
        </Flex>
      ) : (
        <Downshift
          onStateChange={handdleStateChange}
          itemToString={itemToString}
          initialInputValue={value}
          inputValue={value}
          initialSelectedItem={selected}
          isOpen={menuOpen}
          onUserAction={(changes) => {
            if (
              isLeadingInput &&
              changes.type === "__autocomplete_mouseup__" &&
              changes.inputValue === "" &&
              value !== ""
            ) {
              setValue(value);
            }
          }}
          onInputValueChange={(inputValue) => {
            setValue(inputValue);
          }}
        >
          {({
            getLabelProps,
            getInputProps,
            getMenuProps,
            getItemProps,
            isOpen,
            highlightedIndex,
            selectItem,
            clearSelection,
            inputValue,
          }) => (
            <div>
              <Flex
                w="100%"
                justifyContent="space-between"
                alignItems="center"
                position="relative"
              >
                <FormLabel {...getLabelProps({ style: { display: "none" } })}>
                  Comics
                </FormLabel>
                {!!selected && !!selected.thumbnail && !isLeadingInput && (
                  <Image
                    src={selected.thumbnail}
                    alt={selected.title}
                    style={{
                      width: "30px",
                      height: "50px",
                      marginRight: "10px",
                      marginLeft: "10px",
                    }}
                  />
                )}
                <Box position="relative" w="100%">
                  <Input
                    {...getInputProps({
                      onBlur: () => !isLeadingInput && setEditMode(false),
                      onKeyDown: isLeadingInput ? handleKeyDown : null,
                      autoFocus: false,
                      placeholder: "Search for a comic",
                      ...(isLeadingInput ? {} : { width: "100%" }),
                    })}
                  />
                  {isOpen && (
                    <List
                      position="absolute"
                      borderRadius="lg"
                      top="45"
                      left="0"
                      w="100%"
                      zIndex="dropdown"
                      {...getMenuProps({
                        style: { maxHeight: 300, overflowY: "scroll" },
                      })}
                    >
                      {!loading &&
                        suggestions.length === 0 &&
                        inputValue !== "" && (
                          <ListItem>
                            <Box
                              bg={"gray.900"}
                              color="white"
                              display="flex"
                              alignItems="center"
                              p="2"
                            >
                              <Text textColor="white.800">
                                No results found for{" "}
                                <strong>{inputValue}</strong>
                              </Text>
                            </Box>
                          </ListItem>
                        )}
                      {loading ? (
                        <ListItem>
                          <Box
                            bg={"gray.900"}
                            color="white"
                            display="flex"
                            alignItems="center"
                            p="2"
                          >
                            <Spinner />
                          </Box>
                        </ListItem>
                      ) : (
                        suggestions.map((item, index) => (
                          <ListItem
                            {...getItemProps({
                              item,
                              key: item.id,
                            })}
                            onClick={() => selectItem(item)}
                          >
                            <Box
                              bg={
                                highlightedIndex === index
                                  ? "blue.500"
                                  : "gray.900"
                              }
                              color="white"
                              display="flex"
                              alignItems="center"
                              p="2"
                              _hover={{ bg: "gray.600" }}
                            >
                              {item.thumbnail && (
                                <Image
                                  src={item.thumbnail}
                                  alt={item.title}
                                  style={{
                                    width: "30px",
                                    height: "50px",
                                    marginRight: "10px",
                                  }}
                                />
                              )}
                              <Box>{item.title}</Box>
                            </Box>
                          </ListItem>
                        ))
                      )}
                    </List>
                  )}
                </Box>
              </Flex>
            </div>
          )}
        </Downshift>
      )}
    </Box>
  );
};

export default MyAutocomplete;
